import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  Badge,
  Fade,
  Container,
  alpha,
  InputBase,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  Announcement,
  Notifications,
  NotificationsActive,
  FilterList,
  Search,
  Sort,
  CalendarToday,
  Person,
  School,
  Event,
  Warning,
  Info,
  CheckCircle,
  Error,
  Share,
  Bookmark,
  BookmarkBorder,
  ExpandMore,
  ExpandLess,
  Add,
  Download,
  Print,
  Email,
  PushPin,
  Visibility,
  Chat,
  AccessTime,
  Attachment,
  Grade,
  Group,
  LocalFireDepartment,
  TrendingUp,
  Whatshot,
  Flag,
  Schedule,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import apiEndpoints from "../apiconfig";
import Pagination from "@mui/material/Pagination";

// Floating animation
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

const ParentAnnouncements = () => {
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [bookmarked, setBookmarked] = useState([]);
  const [expandedAnnouncement, setExpandedAnnouncement] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentGuid, setStudentGuid] = useState(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 4; // adjust to 6 or 8 if needed

  const [counts, setCounts] = useState({
    upcoming: 0,
    expired: 0,
    active: 0,
  });

  const priorityLevels = {
    urgent: { label: "Urgent", color: "#F44336", icon: <Whatshot /> },
    high: { label: "High", color: "#FF9800", icon: <Warning /> },
    medium: { label: "Medium", color: "#FFB300", icon: <Info /> },
    low: { label: "Low", color: "#4CAF50", icon: <CheckCircle /> },
  };

  useEffect(() => {
    const storedStudent = JSON.parse(
      localStorage.getItem("active_student") || "{}",
    );

    if (storedStudent?.guid) {
      setStudentGuid(storedStudent.guid);
    } else {
      console.error("Student not selected");
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("parent_token");

    if (!token) {
      console.error("JWT token missing");
      setLoading(false);
      return;
    }

    if (!studentGuid) return;

    fetch(
      `${apiEndpoints.announcements}?action=list&student_guid=${studentGuid}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.json())
      .then((data) => {
        console.log("API RESPONSE:", data); // ✅ DEBUG
        if (data.status) {
          setAnnouncements(data.announcements);
          setCounts(data.counts);
        } else {
          console.error(data.message);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [studentGuid]);

  const handleBookmark = (id) => {
    if (bookmarked.includes(id)) {
      setBookmarked(bookmarked.filter((item) => item !== id));
    } else {
      setBookmarked([...bookmarked, id]);
    }
  };

  const handleExpand = (id) => {
    setExpandedAnnouncement(expandedAnnouncement === id ? null : id);
  };

  const filteredAnnouncements = announcements
    .filter((announcement) => {
      const today = new Date().setHours(0, 0, 0, 0);
      const deadline = announcement.deadline
        ? new Date(announcement.deadline).getTime()
        : null;

      // ✅ ALL → SHOW EVERYTHING
      if (filter === "all") {
        return true;
      }

      // 🟢 ACTIVE ONLY
      if (filter === "active") {
        if (!deadline) return true;
        return deadline >= today;
      }

      // 🔴 EXPIRED ONLY
      if (filter === "expired") {
        if (!deadline) return false;
        return deadline < today;
      }

      return true;
    })

    .filter((announcement) => {
      if (!searchQuery) return true;
      return (
        announcement.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        announcement.content
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        announcement.author.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      // 1️⃣ Newest / Oldest
      if (sortBy === "newest") return dateB - dateA;
      if (sortBy === "oldest") return dateA - dateB;

      const today = new Date().setHours(0, 0, 0, 0);
      const deadlineA = a.deadline ? new Date(a.deadline).getTime() : null;
      const deadlineB = b.deadline ? new Date(b.deadline).getTime() : null;

      // 2️⃣ Expiry Soon (Active first, nearest deadline)
      if (sortBy === "expirySoon") {
        if (!deadlineA && !deadlineB) return 0;
        if (!deadlineA) return 1;
        if (!deadlineB) return -1;
        return deadlineA - deadlineB;
      }

      // 3️⃣ Expired First
      if (sortBy === "expiredFirst") {
        const isExpiredA = deadlineA && deadlineA < today;
        const isExpiredB = deadlineB && deadlineB < today;

        if (isExpiredA && !isExpiredB) return -1;
        if (!isExpiredA && isExpiredB) return 1;

        return dateB - dateA; // fallback
      }

      return 0;
    });

  const Meta = ({ icon, text }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {React.cloneElement(icon, {
        fontSize: "small",
        sx: { color: "#888" },
      })}
      <Typography variant="caption" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );
const paginatedAnnouncements = filteredAnnouncements.slice(
  (page - 1) * rowsPerPage,
  page * rowsPerPage,
);
useEffect(() => {
  setPage(1);
}, [filter, searchQuery, sortBy]);

  const getPriority = (level) => priorityLevels[level];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
          url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: 100,
          right: 100,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(120, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 100,
          left: 100,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 1.5, sm: 2, md: 4 },
        }}
      >
        {/* HEADER */}
        <Fade in timeout={800}>
          <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.125rem" },
                  }}
                >
                  Announcements
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  Stay updated with school news, events, and important
                  information
                </Typography>
              </Box>
              {/* <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Badge badgeContent={unreadCount} color="error">
                  <Notifications sx={{ fontSize: 28, color: "#1976D2" }} />
                </Badge>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  sx={{
                    bgcolor: "#1976D2",
                    borderRadius: "25px",
                    px: 3,
                    py: 1,
                    fontWeight: 500,
                    "&:hover": {
                      bgcolor: "#1565C0",
                    },
                  }}
                >
                  Create Alert
                </Button>
              </Box> */}
            </Box>
          </Box>
        </Fade>

        {/* QUICK STATS */}
        <Fade in timeout={900}>
          <Grid container spacing={2} sx={{ mb: 4 }} alignItems="stretch">
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2, sm: 3 },
                  minHeight: 120,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    mb: 1,
                    color: "#1976D2",
                    fontSize: { xs: "1.8rem", sm: "2.2rem" },
                  }}
                >
                  {announcements.length}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Total Announcements
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2, sm: 3 },
                  minHeight: 120,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    mb: 1,
                    color: "#F44336",
                    fontSize: { xs: "1.8rem", sm: "2.2rem" },
                  }}
                >
                  {counts.upcoming}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Upcoming Events
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2, sm: 3 },
                  minHeight: 120,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    mb: 1,
                    color: "#FFB300",
                    fontSize: { xs: "1.8rem", sm: "2.2rem" },
                  }}
                >
                  {counts.active}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Active Events
                </Typography>
              </Paper>
            </Grid>
            <Grid
              item
              sx={{
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "50%",
                  lg: "25%",
                  xl: "25%",
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2, sm: 3 },
                  minHeight: 120,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    mb: 1,
                    color: "#4CAF50",
                    fontSize: { xs: "1.8rem", sm: "2.2rem" },
                  }}
                >
                  {counts.expired}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Expired Events
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* FILTERS AND SEARCH */}
        <Fade in timeout={1000}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              mx: { md: 4 },
              p: 3,
              borderRadius: "20px",
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.1)",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                    }}
                  >
                    <Search
                      sx={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#666",
                      }}
                    />
                    <InputBase
                      placeholder="Search announcements..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{
                        pl: 5,
                        pr: 2,
                        py: 1,
                        width: "100%",
                        borderRadius: "12px",
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        fontSize: "0.9rem",
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    width: "100%",
                    justifyContent: { sm: "flex-end" }, // 🔥 keeps inside
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 160 },
                      maxWidth: { sm: 200 }, // 🔥 prevents overflow
                    }}
                  >
                    <InputLabel>Filter by</InputLabel>
                    <Select
                      value={filter}
                      label="Filter by"
                      onChange={(e) => setFilter(e.target.value)}
                      sx={{ borderRadius: "12px" }}
                    >
                      <MenuItem value="all">All Announcements</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", sm: 160 },
                      maxWidth: { sm: 220 }, // 🔥 wider for text
                    }}
                  >
                    <InputLabel>Sort by</InputLabel>
                    <Select
                      value={sortBy}
                      label="Sort by"
                      onChange={(e) => setSortBy(e.target.value)}
                      sx={{
                        borderRadius: "12px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <MenuItem value="newest">Newest First</MenuItem>
                      <MenuItem value="oldest">Oldest First</MenuItem>
                      <MenuItem value="expirySoon">
                        Expiry Date (Soon First)
                      </MenuItem>
                      <MenuItem value="expiredFirst">Expired First</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>

            {/* Category Filter Chips */}
          </Paper>
        </Fade>

        {/* ANNOUNCEMENTS LIST */}
        <Fade in timeout={1100}>
          <Box sx={{ px: { md: 4 } }}>
            {loading ? (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  borderRadius: "20px",
                  textAlign: "center",
                  background: "rgba(255,255,255,0.6)",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Loading announcements...
                </Typography>
              </Paper>
            ) : filteredAnnouncements.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 8,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                }}
              >
                <Notifications sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                  No announcements found
                </Typography>
                <Typography variant="body2" sx={{ color: "#888" }}>
                  Try adjusting your filters or search terms
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3} mb={3}>
                {paginatedAnnouncements.map((announcement) => {
                  const priority = getPriority(announcement.priority);

                  return (
                    <Grid item xs={12} key={announcement.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: "18px",
                          p: { xs: 2, sm: 2.5 },
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 2,
                          background: "#fff",
                          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                          transition: "0.3s",
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: "0 14px 40px rgba(0,0,0,0.1)",
                          },
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        {/* LEFT ACCENT */}
                        <Box
                          sx={{
                            width: { xs: "100%", sm: 6 },
                            height: { xs: 4, sm: "auto" },
                            borderRadius: "12px",
                            bgcolor: "#1976D2",
                          }}
                        />

                        {/* ICON */}
                        <Box
                          sx={{
                            width: { xs: 40, sm: 48 },
                            height: { xs: 40, sm: 48 },
                            borderRadius: "14px",
                            background:
                              "linear-gradient(135deg, #1976D2, #42A5F5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            flexShrink: 0,
                          }}
                        >
                          <CalendarToday />
                        </Box>

                        {/* MAIN CONTENT */}
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 2,
                            }}
                          >
                            {/* TITLE + STATUS */}
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: "1rem", sm: "1.1rem" },
                                  fontWeight: 700,
                                  color: "#1a1a1a",
                                }}
                              >
                                {announcement.title}
                              </Typography>

                              {/* STATUS BADGE */}
                              <Box
                                sx={{
                                  px: 1.2,
                                  py: 0.3,
                                  borderRadius: "999px",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  bgcolor:
                                    announcement.deadline &&
                                    new Date(announcement.deadline) < new Date()
                                      ? "rgba(244,67,54,0.12)"
                                      : "rgba(76,175,80,0.12)",
                                  color:
                                    announcement.deadline &&
                                    new Date(announcement.deadline) < new Date()
                                      ? "#F44336"
                                      : "#2E7D32",
                                }}
                              >
                                {announcement.deadline &&
                                new Date(announcement.deadline) < new Date()
                                  ? "EXPIRED"
                                  : "ACTIVE"}
                              </Box>
                            </Box>

                            {/* EXPAND ICON */}
                            <IconButton
                              onClick={() =>
                                setExpandedAnnouncement(
                                  expandedAnnouncement === announcement.id
                                    ? null
                                    : announcement.id,
                                )
                              }
                              sx={{
                                bgcolor: "rgba(0,0,0,0.04)",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                              }}
                            >
                              {expandedAnnouncement === announcement.id ? (
                                <ExpandLess />
                              ) : (
                                <ExpandMore />
                              )}
                            </IconButton>
                          </Box>

                          {/* META INFO */}
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.5,
                              mt: 1,
                            }}
                          >
                            <Meta
                              icon={<Person />}
                              text={announcement.author}
                            />
                            <Meta
                              icon={<CalendarToday />}
                              text={new Date(
                                announcement.date,
                              ).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            />
                            {announcement.deadline && (
                              <Chip
                                icon={<Schedule />}
                                label={`Due: ${new Date(
                                  announcement.deadline,
                                ).toLocaleDateString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                })}`}
                                size="small"
                                sx={{
                                  bgcolor: "rgba(244,67,54,0.1)",
                                  color: "#F44336",
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>

                          {/* EXPANDED CONTENT */}
                          <Collapse
                            in={expandedAnnouncement === announcement.id}
                          >
                            <Box
                              sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: "1px dashed rgba(0,0,0,0.1)",
                              }}
                            >
                              <Typography
                                sx={{
                                  lineHeight: 1.7,
                                  color: "#444",
                                  fontSize: { xs: "0.9rem", sm: "0.95rem" },
                                }}
                              >
                                {announcement.content}
                              </Typography>

                              {/* ATTACHMENTS */}
                              {announcement.attachments?.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    display: "flex",
                                    gap: 1,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {announcement.attachments.map((file, i) => (
                                    <Chip
                                      key={i}
                                      icon={<Attachment />}
                                      label={file}
                                      sx={{
                                        bgcolor: "rgba(0,0,0,0.04)",
                                        "&:hover": {
                                          bgcolor: "rgba(0,0,0,0.08)",
                                        },
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {filteredAnnouncements.length > rowsPerPage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  mb: 2,
                }}
              >
                <Pagination
                  count={Math.ceil(filteredAnnouncements.length / rowsPerPage)}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                  size="large"
                />
              </Box>
            )}
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ParentAnnouncements;
